// ThemeContext.js

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

// Styled Components
import { createGlobalStyle } from 'styled-components';

import { useWorkspace } from 'context/WorkspaceContext';
import { useUser } from 'context/UserContext';
import { createSetting, updateSetting } from 'services/settings/SettingWriteService';
import { updateWorkspace } from 'services/workspaces/WorkspaceWriteService';

export const useStyle = createGlobalStyle;

export const ThemeMode = Object.freeze({
    DARK: 'DARK',
    LIGHT: 'LIGHT',
});

const STORAGE_KEY = 'workspace-theme-mode.v1';
const SETTINGS_THEME_OVERRIDE_KEY = 'THEME_MODE_OVERRIDE_BY_WORKSPACE';
const WORKSPACE_DEFAULT_THEME_FIELD = 'themeModeDefault';

const WORKSPACE_COLOR_OPTIONS = [
    '#2f2619',
    '#3a2d1e',
    '#463522',
    '#524029',
    '#5f4930',
    '#6c5334',
    '#795c37',
    '#86653a',
    '#936d3c',
    '#7a5734',
    '#8a5d33',
    '#9a6734',
    '#a16a30',
    '#8d4f2c',
    '#7b4428',
    '#6a3926',
    '#5e2c28',
    '#4b2324',
    '#5e0e10',
    '#620b0d',
    '#74211e',
    '#8b2f23',
    '#9e3c24',
    '#b94d25',
    '#a14a1f',
    '#7b6d2f',
    '#6f6a31',
    '#646430',
    '#5d5f2f',
    '#4f5a31',
    '#44522f',
    '#3a4a2d',
    '#32422b',
    '#556b3c',
    '#647a42',
    '#748949',
    '#5c7140',
    '#4f6539',
    '#435a34',
    '#374e30',
    '#2f4330',
    '#383936',
    '#454546',
    '#55524a',
    '#666666',
    '#4a4149',
    '#435059',
];

const THEME_MAP = {
    [ThemeMode.DARK]: {
        key: ThemeMode.DARK,

        // Screen Background
        background: '#000000',
        //background: '#111111',
        backgroundMobile: '#000000',

        // Overlay
        overlay: '#111111aa',

        // Clickable Items
        backgroundMuted: 'rgba(255, 255, 255, 0.04)',
        backgroundHover: 'rgba(255, 255, 255, 0.06)',
        foreground: '#FFFFFF',
        foregroundMuted: '#FFFFFF66',

        // Outlines and borders
        outline: 'rgba(255, 255, 255, 0.3)',
        outlineMuted: 'rgba(255, 255, 255, 0.2)',

        // Popup
        popup: 'rgba(0, 0, 0, 0.3)',

        // Buttons
        buttonBackgroundColor: '#773b26',
        buttonIconColor: '#FFFFFF66',
        buttonLabelColor: '#FFFFFF',
        buttonHoverBackgroundColor: '#b94d25',
        buttonHoverIconColor: '#FFFFFF',
        buttonHoverLabelColor: '#FFFFFF',

        // Status Indicators
        success: '#b94d25',
        error: '#b94d25',
        active: '#b94d25',
        warning: '#680c0d',

        // General Colors
        red: '#680c0d',
        purple: '#5a3d32',
        blue: '#383936',
        green: 'darkgreen',
        teal: '#666666',
        orange: '#b94d25',
        yellow: '#f4db92',

        // Inputs
        input: '#FFFFFF10',

        // Status Bar
        status: '#FFFFFF33',

        // Panels
        panel: 'rgba(255, 255, 255, 0.05)',

        selectionBackground: '#666666',
        selectionForeground: '#FFFFFF',

        // Color Options (for views, etc.)
        colorOptions: WORKSPACE_COLOR_OPTIONS,
        
        // Highlights
        //highlight: '#b94d25',
        //highlight: '#aeaeae',
        glow: '#773b26',
        highlight: '#773b26',
        highlightForeground: '#FFFFFF',

        enableBackground: true, 
    },
    [ThemeMode.LIGHT]: {
        key: ThemeMode.LIGHT,

        // Screen Background
        background: '#FFFFFF',
        backgroundMobile: '#FFFFFF',

        // Overlay
        overlay: 'rgba(0, 0, 0, 0.2)',

        // Clickable Items
        backgroundMuted: 'rgba(0, 0, 0, 0.03)',
        backgroundHover: 'rgba(0, 0, 0, 0.06)',
        foreground: '#222325',
        foregroundMuted: '#00000088',

        // Outlines and borders
        outline: 'rgba(0, 0, 0, 0.4)',
        outlineMuted: 'rgba(0, 0, 0, 0.1)',
        
        // Popup
        popup: 'rgba(255, 255, 255, 0.7)',

        // Buttons
        buttonBackgroundColor: 'rgba(0, 0, 0, 0.06)',
        buttonIconColor: '#00000066',
        buttonLabelColor: '#000000',
        buttonHoverBackgroundColor: '#00000055',
        buttonHoverIconColor: '#FFFFFF',
        buttonHoverLabelColor: '#FFFFFF',

        // Status Indicators
        success: '#b94d25',
        error: '#b94d25',
        active: '#b94d25',
        warning: '#680c0d',

        // General Colors
        red: '#680c0d',
        purple: '#5a3d32',
        blue: '#383936',
        green: 'green',
        teal: '#666666',
        orange: '#b94d25',
        yellow: '#f4db92',

        // Inputs
        input: 'rgba(0, 0, 0, 0.03)',

        // Status Bar
        status: 'rgba(0, 0, 0, 0.4)',

        // Panels
        panel: 'rgba(0, 0, 0, 0.03)',

        selectionBackground: '#64748B',
        selectionForeground: '#FFFFFF',

        // Color Options (for views, etc.)
        colorOptions: WORKSPACE_COLOR_OPTIONS,
        
        // Highlights
        glow: '#FFFFFF',
        highlight: '#808080',
        highlightForeground: '#FFFFFF',

        enableBackground: false,
    },
};

const ThemeContext = createContext({
    themeMode: ThemeMode.DARK,
    theme: THEME_MAP[ThemeMode.DARK],
    setThemeMode: () => {},
    setThemeModeOverride: () => {},
    clearThemeModeOverride: () => {},
    setWorkspaceDefaultThemeMode: () => {},
    isUsingWorkspaceThemeDefault: true,
    workspaceDefaultThemeMode: ThemeMode.DARK,
    themePreferenceSource: 'fallback',
    themes: THEME_MAP,
});

const readStoredPreferences = () => {
    try {
        if (typeof window === 'undefined') return {};
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed ? parsed : {};
    } catch (err) {
        console.warn('ThemeContext: failed to read workspace theme preferences', err);
        return {};
    }
};

function normalizeThemeMode(value) {
    return THEME_MAP[value] ? value : null;
}

function getWorkspaceThemeOverrideMap(userSettings) {
    const candidate = userSettings?.[SETTINGS_THEME_OVERRIDE_KEY];
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return {};
    }

    return candidate;
}

/**
 * ThemeOverrideProvider
 *
 * Applies a nested theme mode override while preserving the parent theme actions.
 *
 * @param {ReactNode} children - Nested content that should inherit the override.
 * @param {string|null} themeMode - Optional theme mode override to apply.
 *
 * @returns {ReactNode} The nested provider when an override is present.
 */
export const ThemeOverrideProvider = ({
    children,
    themeMode = null,
}) => {
    const parentValue = useContext(ThemeContext);

    const overriddenValue = useMemo(() => {
        const normalizedThemeMode = normalizeThemeMode(themeMode);

        if (!normalizedThemeMode) {
            return parentValue;
        }

        return {
            ...parentValue,
            isUsingWorkspaceThemeDefault: true,
            theme: THEME_MAP[normalizedThemeMode] || THEME_MAP[ThemeMode.DARK],
            themeMode: normalizedThemeMode,
            themePreferenceSource: 'public-override',
            workspaceDefaultThemeMode: normalizedThemeMode,
            themes: THEME_MAP,
        };
    }, [parentValue, themeMode]);

    if (!normalizeThemeMode(themeMode)) {
        return children;
    }

    return (
        <ThemeContext.Provider value={overriddenValue}>
            {children}
        </ThemeContext.Provider>
    );
};

export const ThemeProvider = ({ children }) => {
    const { activeWorkspace, setActiveWorkspace } = useWorkspace();
    const { user, userSettings, setUserSettings } = useUser();
    const migratedLegacyWorkspaceRef = useRef(new Set());

    const activeWorkspaceKey = typeof activeWorkspace?.key === 'string'
        ? activeWorkspace.key.trim()
        : '';

    const userKey = typeof user?.key === 'string'
        ? user.key.trim()
        : '';

    const themeModeByWorkspaceOverride = useMemo(() => {
        return getWorkspaceThemeOverrideMap(userSettings);
    }, [userSettings]);

    const workspaceThemeOverride = useMemo(() => {
        if (!activeWorkspaceKey) return null;
        return normalizeThemeMode(themeModeByWorkspaceOverride?.[activeWorkspaceKey]);
    }, [activeWorkspaceKey, themeModeByWorkspaceOverride]);

    const workspaceDefaultThemeModeRaw = useMemo(() => {
        return normalizeThemeMode(activeWorkspace?.[WORKSPACE_DEFAULT_THEME_FIELD]);
    }, [activeWorkspace]);

    const workspaceDefaultThemeMode = useMemo(() => {
        return workspaceDefaultThemeModeRaw || ThemeMode.DARK;
    }, [workspaceDefaultThemeModeRaw]);

    const legacyWorkspaceThemeMode = useMemo(() => {
        if (!activeWorkspaceKey) return null;
        if (workspaceThemeOverride) return null;
        if (workspaceDefaultThemeModeRaw) return null;

        const prefs = readStoredPreferences();
        return normalizeThemeMode(prefs?.[activeWorkspaceKey]);
    }, [activeWorkspaceKey, workspaceDefaultThemeModeRaw, workspaceThemeOverride]);

    const themeMode = workspaceThemeOverride
        || workspaceDefaultThemeModeRaw
        || legacyWorkspaceThemeMode
        || ThemeMode.DARK;

    const isUsingWorkspaceThemeDefault = !workspaceThemeOverride;

    const themePreferenceSource = workspaceThemeOverride
        ? 'override'
        : (workspaceDefaultThemeModeRaw ? 'workspace-default' : (legacyWorkspaceThemeMode ? 'legacy-local' : 'fallback'));

    const persistUserThemeSettings = useCallback(async (nextOverrideMap) => {
        if (!userKey) return;

        const patch = {
            [SETTINGS_THEME_OVERRIDE_KEY]: nextOverrideMap,
        };

        try {
            if (userSettings?.id || userSettings?.userKey) {
                await updateSetting(userKey, patch);
                return;
            }

            await createSetting(userKey, patch);
        } catch (error) {
            if (error?.code === 'not-found') {
                await createSetting(userKey, patch);
                return;
            }
            throw error;
        }
    }, [userKey, userSettings?.id, userSettings?.userKey]);

    const setThemeModeOverride = useCallback(async (nextMode) => {
        if (!activeWorkspaceKey || !userKey) return;

        const normalizedMode = normalizeThemeMode(nextMode) || ThemeMode.DARK;
        const nextOverrideMap = {
            ...getWorkspaceThemeOverrideMap(userSettings),
            [activeWorkspaceKey]: normalizedMode,
        };

        if (typeof setUserSettings === 'function') {
            setUserSettings((previous) => ({
                ...(previous || {}),
                userKey,
                [SETTINGS_THEME_OVERRIDE_KEY]: {
                    ...getWorkspaceThemeOverrideMap(previous),
                    [activeWorkspaceKey]: normalizedMode,
                },
            }));
        }

        try {
            await persistUserThemeSettings(nextOverrideMap);
        } catch (error) {
            console.error('ThemeContext: failed to save theme override', error);
        }
    }, [activeWorkspaceKey, persistUserThemeSettings, setUserSettings, userKey, userSettings]);

    const clearThemeModeOverride = useCallback(async () => {
        if (!activeWorkspaceKey || !userKey) return;

        const nextOverrideMap = {
            ...getWorkspaceThemeOverrideMap(userSettings),
        };
        delete nextOverrideMap[activeWorkspaceKey];

        if (typeof setUserSettings === 'function') {
            setUserSettings((previous) => {
                const previousMap = {
                    ...getWorkspaceThemeOverrideMap(previous),
                };
                delete previousMap[activeWorkspaceKey];

                return {
                    ...(previous || {}),
                    userKey,
                    [SETTINGS_THEME_OVERRIDE_KEY]: previousMap,
                };
            });
        }

        try {
            await persistUserThemeSettings(nextOverrideMap);
        } catch (error) {
            console.error('ThemeContext: failed to clear theme override', error);
        }
    }, [activeWorkspaceKey, persistUserThemeSettings, setUserSettings, userKey, userSettings]);

    const setWorkspaceDefaultThemeMode = useCallback(async (nextMode) => {
        if (!activeWorkspaceKey) return;

        const normalizedMode = normalizeThemeMode(nextMode) || ThemeMode.DARK;

        if (typeof setActiveWorkspace === 'function') {
            setActiveWorkspace((previous) => {
                if (!previous) return previous;

                const previousKey = typeof previous?.key === 'string'
                    ? previous.key.trim()
                    : '';

                if (previousKey !== activeWorkspaceKey) return previous;

                return {
                    ...previous,
                    [WORKSPACE_DEFAULT_THEME_FIELD]: normalizedMode,
                };
            });
        }

        try {
            await updateWorkspace(activeWorkspaceKey, {
                [WORKSPACE_DEFAULT_THEME_FIELD]: normalizedMode,
            });
        } catch (error) {
            console.error('ThemeContext: failed to update workspace default theme mode', error);
        }
    }, [activeWorkspaceKey, setActiveWorkspace]);

    useEffect(() => {
        if (!activeWorkspaceKey || !userKey) return;
        if (workspaceThemeOverride) return;
        if (workspaceDefaultThemeModeRaw) return;

        const legacyMode = legacyWorkspaceThemeMode;
        if (!legacyMode) return;

        const migrationKey = `${userKey}:${activeWorkspaceKey}`;
        if (migratedLegacyWorkspaceRef.current.has(migrationKey)) return;
        migratedLegacyWorkspaceRef.current.add(migrationKey);

        const nextOverrideMap = {
            ...getWorkspaceThemeOverrideMap(userSettings),
            [activeWorkspaceKey]: legacyMode,
        };

        if (typeof setUserSettings === 'function') {
            setUserSettings((previous) => ({
                ...(previous || {}),
                userKey,
                [SETTINGS_THEME_OVERRIDE_KEY]: {
                    ...getWorkspaceThemeOverrideMap(previous),
                    [activeWorkspaceKey]: legacyMode,
                },
            }));
        }

        (async () => {
            try {
                await persistUserThemeSettings(nextOverrideMap);
            } catch (error) {
                console.error('ThemeContext: failed to migrate legacy workspace theme preference', error);
            }
        })();
    }, [
        activeWorkspaceKey,
        legacyWorkspaceThemeMode,
        persistUserThemeSettings,
        setUserSettings,
        userKey,
        userSettings,
        workspaceDefaultThemeModeRaw,
        workspaceThemeOverride,
    ]);

    const value = useMemo(() => ({
        themeMode,
        theme: THEME_MAP[themeMode] || THEME_MAP[ThemeMode.DARK],
        setThemeMode: setThemeModeOverride,
        setThemeModeOverride,
        clearThemeModeOverride,
        setWorkspaceDefaultThemeMode,
        isUsingWorkspaceThemeDefault,
        workspaceDefaultThemeMode,
        themePreferenceSource,
        themes: THEME_MAP,
    }), [
        clearThemeModeOverride,
        isUsingWorkspaceThemeDefault,
        setThemeModeOverride,
        setWorkspaceDefaultThemeMode,
        themeMode,
        themePreferenceSource,
        workspaceDefaultThemeMode,
    ]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);